import React, { useState, useEffect } from "react";
import { Typography, Box, Divider, Tabs, Tab, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import TemplatesMenu from "./templatesMenu";
import TicketsService from "../api/tickets";
import { TemplateInputs } from "../common/templateInputs";
import Breadcrumbs from "../common/breadcrumbs";
import AlertDialog from "../common/AlertDialog";
import { useNavigate } from "react-router-dom";
import handleNavigate from "../common/customFunctions/handleNavigate";
import useTheme from "@mui/material/styles/useTheme";

const breadcrumbs = [
    { id: 0, name: "Home", href: "./" },
    { id: 1, name: "Ticket Dashboard", href: "indexHelpdesk" },
    { id: 2, name: "NewTicket", href: "NewTicket" }
];

function NewTicket() {
    const [value, setValue] = useState(0);
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useTranslation();
    const [selected, setSelected] = useState(undefined);
    const [requirements, setRequirements] = useState(undefined);
    const [departmentsList, setDepartmentsList] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(undefined);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState("confirm");
    const [dialogTitle, setDialogTitle] = useState("");
    const [saveLoading, setSaveLoading] = useState(false);

    useEffect(() => {
        const apiKey = localStorage.getItem("apiKey");
        if (apiKey) {
            TicketsService.GetOperationDepartment({
                department_id: "",
                operation_profile_uid: "",
                name: "",
                status: "",
                apiKey
            }).then((json) => {
                if (json && json.department && json.department.length > 0) {
                    let DepList = [{ id: "", name: "General" }];
                    for (let index = 0; index < json.department.length; index++) {
                        DepList.push({
                            id: json.department[index].department_id,
                            name: json.department[index].name
                        });
                    }
                    setDepartmentsList(DepList);
                }
            });
        }
    }, []);

    useEffect(() => {
        const apiKey = localStorage.getItem("apiKey");
        if (selected !== undefined && apiKey) {
            TicketsService.GetTemplateInputs(selected, apiKey).then((json) => {
                if (json) setRequirements(json);
            });
        }
    }, [selected]);

    const handleTabChange = (event, newValue) => {
        setValue(newValue);
        setSelectedDepartment(newValue === 0 ? "" : departmentsList[newValue]?.id);
    };
    const handleInputChange = (inputUid, checked) => {
        if (!requirements) return;
        const reqs = JSON.parse(JSON.stringify(requirements));
        reqs.ticket_template[0].requirements.forEach((requirement) => {
            requirement.inputs.forEach((input) => {
                if (input.template_requirement_input_uid?.toString() === inputUid.toString()) {
                    input.value = checked;

                }
            });
        });
        setRequirements(reqs);
    };


    const handleSaveTicket = () => {
        setDialogTitle("Are you sure you want to create this ticket?");
        setDialogType("confirm");
        setDialogOpen(true);
    };
    function transformRequirementsForApi(requirements) {
        const cloned = JSON.parse(JSON.stringify(requirements));
        cloned.ticket_template[0].requirements.forEach((requirement) => {
            requirement.inputs.forEach((input) => {
                if (Number(input.input_requirement_config_uid) === 6) {
                    input.value = Array.isArray(input.value) ? input.value : [];
                } else {
                    input.value = input.value ?? "";
                }
            });
        });
        return cloned;
    }

    const handleDoSave = async () => {
        setSaveLoading(true);
        try {
            const apiKey = localStorage.getItem("apiKey");
            const req = transformRequirementsForApi(requirements);
            req.ticket_template[0].department_id = selectedDepartment === undefined ? "" : selectedDepartment;
            req.ticket_template[0].status = "new";
            req.ticket_template[0].ticket_header_uid = "";
            req.ticket_template[0].operation_profile_uid = JSON.parse(localStorage.getItem("defaultOrganization"))?.uid;
            req.ticket_template[0].ticket_template_header_uid = req.ticket_template[0].template_header_uid;
            req.ticket_template[0].created_by_person_uid = JSON.parse(localStorage.getItem("user")).profileUid;
            delete req.ticket_template[0].template_header_uid;
            await TicketsService.SaveTicketAPI(req.ticket_template[0], apiKey);
            setDialogType("success");
            setDialogTitle("Ticket created successfully!");
            setSaveLoading(false);
        } catch (e) {
            setDialogType("error");
            setDialogTitle("Failed to create ticket. Please check all fields and try again.");
            setSaveLoading(false);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSaveLoading(false);
        if (dialogType === "success") {
            handleNavigate(event, navigate, "/myTickets");
        }
    };

    const handlePopulate = (requirements) => {
        if (requirements && requirements.count > 0) {
            return (
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: "36px", mb: 0 }}>{requirements.ticket_template[0].name}</Typography>
                    <Divider sx={{ my: 2 }} />
                    {requirements.ticket_template[0].requirements.map((requirement, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: "22px", mb: 1 }}>{requirement.name}</Typography>
                            <Box>
                                {requirement.inputs.map((input, idx) => (
                                    <TemplateInputs key={idx} inputs={input} onChange={handleInputChange} />
                                ))}
                            </Box>
                            <Divider sx={{ my: 2 }} />
                        </Box>
                    ))}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button variant="contained" onClick={handleSaveTicket} disabled={saveLoading}>
                            {saveLoading ? "Saving..." : t("Save")}
                        </Button>
                    </Box>
                </Box>
            );
        }
        return null;
    };

    return (
        <Box sx={{ p: 2, minHeight: "90vh", height: "fit-content" }}>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <Typography sx={{ fontSize: "45px", mb: 0 }} variant="h2" gutterBottom>
                {t("NewTicket")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ width: "100%" }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile value={value} onChange={handleTabChange}>
                        {departmentsList && departmentsList.length > 0 && departmentsList.map((item, idx) => <Tab label={item.name} key={idx} />)}
                    </Tabs>
                </Box>
                {departmentsList &&
                    departmentsList.length > 0 &&
                    departmentsList.map((item, index) => (
                        <div hidden={value !== index} key={index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`}>
                            {value === index && (
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.custom.borderColor}`,
                                        minHeight: "50vh"
                                    }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            flexWrap: "wrap"
                                        }}>
                                        <Typography sx={{ fontSize: "32px" }} gutterBottom>
                                            {item.name}
                                        </Typography>
                                        <TemplatesMenu setSelected={setSelected} department={Number(item.id)} />
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                    {selected !== undefined ? handlePopulate(requirements) : null}
                                </Box>
                            )}
                        </div>
                    ))}
            </Box>
            <AlertDialog open={dialogOpen} dialogTitle={dialogTitle} buttonLabel={dialogType === "confirm" ? "Confirm" : dialogType === "success" ? "OK" : "Close"} cancelLabel={dialogType === "confirm" ? "Cancel" : ""} saveLoading={saveLoading} onClose={handleDialogClose} onDoFunction={dialogType === "confirm" ? handleDoSave : handleDialogClose} />
        </Box>
    );
}

export default NewTicket;
